import { useCallback, useEffect, useState } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import { Base } from "../../framework/base";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { handleApiError } from "../../models/store/storeEffects";
import {
    fetchReportVehicles,
    fetchReportEmployees,
    fetchReportCostCenters,
    fetchReportCustomers,
} from "../../store/reportOptionsSlice";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { ICostCenterItem } from "../../models/costCenter/costCenterItem";
import { ICustomerItem } from "../../models/customer/customerItem";
import { IVehicleGroupItem } from "../../models/vehicleGroup/vehicleGroupItem";
import { DriverLogRow } from "../../models/vehicle/driverLogRow";
import { getDriverLog } from "../../services/vehicleService";
import { getVehicleGroupItems } from "../../services/vehicleGroupService";

export interface DriverLogData {
    loading: boolean;
    rows: DriverLogRow[];
    vehicles: VehicleListItemDto[];
    vehicleGroups: IVehicleGroupItem[];
    employees: EmployeeListItemDto[];
    costCenters: ICostCenterItem[];
    customers: ICustomerItem[];
    filterByVehicles: (vehicleId: string) => boolean;
    filterByEmployees: (employeeId: string | null) => boolean;
    filterByCostCenters: (costCenterId: string | null) => boolean;
    filterByCustomers: (customerId: string | null) => boolean;
}

export const useDriverLogData = (): DriverLogData => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<DriverLogRow[]>([]);
    const [vehicleGroups, setVehicleGroups] = useState<IVehicleGroupItem[]>([]);

    const timeRange = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.timeRange
    );
    const selectedVehicleGroups = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedVehicleGroups
    );
    const selectedVehicles = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedVehicles
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedEmployees
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedCostCenters
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedCustomers
    );

    const vehicles = useAppSelector(
        (state) => state.reportOptions.vehicle.items
    );
    const employees = useAppSelector((state) => state.reportOptions.employees);
    const costCenters = useAppSelector(
        (state) => state.reportOptions.costCenters
    );
    const customers = useAppSelector(
        (state) => state.reportOptions.customer.items
    );

    useEffect(() => {
        const promises = [
            dispatch(fetchReportVehicles() as unknown as AnyAction),
            dispatch(fetchReportEmployees() as unknown as AnyAction),
            dispatch(fetchReportCostCenters() as unknown as AnyAction),
            dispatch(fetchReportCustomers() as unknown as AnyAction),
        ];
        void getVehicleGroupItems(1000, 1, "", "name", true)
            .then((res) => setVehicleGroups(res?.items ?? []))
            .catch((err: unknown) => handleApiError(err, dispatch));
        return () => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
            promises.forEach((p) => p.abort());
        };
    }, []);

    useEffect(() => {
        if (!selectedVehicles || selectedVehicles.length === 0) {
            setRows([]);
            return;
        }

        const isAllVehicles = selectedVehicles.includes("__all__");

        // When "all vehicles" is selected with vehicle groups, expand to
        // filtered vehicle IDs. Without groups, send empty array — the
        // backend treats empty vehicleIds as "all vehicles".
        let vehicleIdsForQuery: string[];
        if (isAllVehicles) {
            if (selectedVehicleGroups?.length > 0) {
                // Wait for vehicles to load before we can filter by group
                if (!vehicles || vehicles.length === 0) {
                    setRows([]);
                    return;
                }
                vehicleIdsForQuery = vehicles
                    .filter((v) =>
                        selectedVehicleGroups.includes(v.vehicleGroupId)
                    )
                    .map((v) => v.id);
                if (vehicleIdsForQuery.length === 0) {
                    setRows([]);
                    return;
                }
            } else {
                // No group filter — backend handles "all" with empty array
                vehicleIdsForQuery = [];
            }
        } else {
            vehicleIdsForQuery = selectedVehicles.filter(
                (id) => id !== "__all__"
            );
            if (vehicleIdsForQuery.length === 0) {
                setRows([]);
                return;
            }
        }

        const abortController = new AbortController();
        let isActive = true;

        setLoading(true);

        getDriverLog(
            {
                vehicleIds: vehicleIdsForQuery,
                windowStartTime: Base.dayjsToJsonDateTimeOffset(timeRange[0]),
                windowEndTime: Base.dayjsToJsonDateTimeOffset(
                    Base.dateEndOf(timeRange[1], "day")
                ),
            },
            abortController.signal
        )
            .then((res) => {
                if (isActive) {
                    setRows(res?.data ?? []);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (err?.name !== "AbortError" && isActive) {
                    handleApiError(err, dispatch);
                    setLoading(false);
                }
            });

        return () => {
            isActive = false;
            abortController.abort();
        };
    }, [
        timeRange,
        selectedVehicleGroups,
        selectedVehicles,
        vehicles,
    ]);

    const filterByVehicles = useCallback(
        (vehicleId: string) => {
            if (!selectedVehicles || selectedVehicles.length === 0) return true;
            if (selectedVehicles.includes("__all__")) return true;
            return selectedVehicles.includes(vehicleId);
        },
        [selectedVehicles]
    );

    const filterByEmployees = useCallback(
        (employeeId: string | null) => {
            if (!selectedEmployees || selectedEmployees.length === 0)
                return true;
            if (employeeId === null) return false;
            return selectedEmployees.includes(employeeId);
        },
        [selectedEmployees]
    );

    const filterByCostCenters = useCallback(
        (costCenterId: string | null) => {
            if (!selectedCostCenters || selectedCostCenters.length === 0)
                return true;
            if (costCenterId === null) return false;
            return selectedCostCenters.includes(costCenterId);
        },
        [selectedCostCenters]
    );

    const filterByCustomers = useCallback(
        (customerId: string | null) => {
            if (!selectedCustomers || selectedCustomers.length === 0)
                return true;
            if (customerId === null) return false;
            return selectedCustomers.includes(customerId);
        },
        [selectedCustomers]
    );

    return {
        loading,
        rows,
        vehicles,
        vehicleGroups,
        employees,
        costCenters,
        customers,
        filterByVehicles,
        filterByEmployees,
        filterByCostCenters,
        filterByCustomers,
    };
};
